import { useEffect, useRef, useCallback } from 'react';

import { clamp } from '../utils/pdfEditorUtils';
import { logger } from '../utils/logger';

import type { RefObject, SetStateAction, Dispatch } from 'react';

interface UsePDFViewportControlsParams {
  containerRef: RefObject<HTMLDivElement | null>;
  numPages: number;
  isViewportChanging: boolean;
  setIsZooming: Dispatch<SetStateAction<boolean>>;
  setIsScrolling: Dispatch<SetStateAction<boolean>>;
  setScale: Dispatch<SetStateAction<number>>;
  setVisiblePages: Dispatch<SetStateAction<Set<number>>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

export const usePDFViewportControls = ({
  containerRef,
  numPages,
  isViewportChanging,
  setIsZooming,
  setIsScrolling,
  setScale,
  setVisiblePages,
  setCurrentPage,
}: UsePDFViewportControlsParams) => {
  const wheelZoomActive = useRef(false);
  const touchZoomActive = useRef(false);
  const lastZoomUpdate = useRef(0);
  const pendingZoomDelta = useRef(0);
  const zoomRafId = useRef<number | null>(null);
  const zoomEndTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollEndTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRafId = useRef<number | null>(null);
  const lastPageRef = useRef(1);

  const updateCurrentPageFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || !numPages) return;

    const pageElements = Array.from(
      container.querySelectorAll('[data-page-number]'),
    ) as HTMLElement[];

    if (pageElements.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const viewportMiddle = containerRect.top + container.clientHeight / 2;

    let bestPage = 1;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const el of pageElements) {
      const rect = el.getBoundingClientRect();
      const pageCenter = rect.top + rect.height / 2;

      const distance = Math.abs(pageCenter - viewportMiddle);

      const pageNum = parseInt(el.getAttribute('data-page-number') || '1');

      if (distance < bestDistance) {
        bestDistance = distance;
        bestPage = pageNum;
      }
    }

    if (bestPage !== lastPageRef.current) {
      lastPageRef.current = bestPage;
      setCurrentPage(bestPage);
    }
  }, [containerRef, numPages, setCurrentPage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (touchZoomActive.current) return;

      e.preventDefault();

      const delta = -Math.sign(e.deltaY) * 0.05;
      pendingZoomDelta.current += delta;

      const now = Date.now();
      if (now - lastZoomUpdate.current < 16) {
        logger.debug(
          `[PDFEditor] Wheel zoom throttled (${
            now - lastZoomUpdate.current
          }ms since last) @ ${now}`,
        );
        return;
      }

      logger.debug(`[PDFEditor] Wheel zoom triggered @ ${now}`);
      wheelZoomActive.current = true;
      setIsZooming(true);
      lastZoomUpdate.current = now;

      const currentDelta = pendingZoomDelta.current;
      pendingZoomDelta.current = 0;

      setScale((prev) => {
        const newScale = clamp(prev + currentDelta, 0.5, 3.0);
        if (Math.abs(newScale - prev) > 0.001) {
          logger.debug(
            `[PDFEditor] Scale: ${prev.toFixed(2)} → ${newScale.toFixed(
              2,
            )} @ ${Date.now()}`,
          );
        }
        return newScale;
      });

      if (zoomEndTimeout.current) clearTimeout(zoomEndTimeout.current);
      zoomEndTimeout.current = setTimeout(() => {
        logger.debug('[PDFEditor] Wheel zoom ended');
        wheelZoomActive.current = false;
        setIsZooming(false);
      }, 300);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (zoomRafId.current !== null) {
        cancelAnimationFrame(zoomRafId.current);
        zoomRafId.current = null;
      }
      if (zoomEndTimeout.current) {
        clearTimeout(zoomEndTimeout.current);
        zoomEndTimeout.current = null;
      }
    };
  }, [containerRef, setIsZooming, setScale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let initialDistance = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const distance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
        initialDistance = distance;
        touchZoomActive.current = true;
        setIsZooming(true);
        logger.debug('[PDFEditor] Pinch zoom started');
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !initialDistance) return;
      if (wheelZoomActive.current) return;

      const now = Date.now();
      if (now - lastZoomUpdate.current < 100) return;

      e.preventDefault();

      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );

      const scaleRatio = distance / initialDistance;
      lastZoomUpdate.current = now;
      logger.debug(`[PDFEditor] Pinch zoom: ratio ${scaleRatio.toFixed(2)}`);

      setScale((prev) => {
        const newScale = prev * scaleRatio;
        const clampedScale = clamp(newScale, 0.5, 3.0);
        logger.debug(
          `[PDFEditor] Scale: ${prev.toFixed(2)} → ${clampedScale.toFixed(2)}`,
        );
        return clampedScale;
      });

      initialDistance = distance;
    };

    const handleTouchEnd = () => {
      if (initialDistance !== 0) {
        logger.debug('[PDFEditor] Pinch zoom ended');
      }
      initialDistance = 0;
      touchZoomActive.current = false;
      setIsZooming(false);
    };

    container.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [containerRef, setIsZooming, setScale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (wheelZoomActive.current || touchZoomActive.current) return;

      setIsScrolling(true);
      if (scrollRafId.current) {
        cancelAnimationFrame(scrollRafId.current);
      }
      scrollRafId.current = requestAnimationFrame(() => {
        updateCurrentPageFromScroll();
      });

      if (scrollEndTimeout.current) clearTimeout(scrollEndTimeout.current);

      scrollEndTimeout.current = setTimeout(() => {
        setIsScrolling(false);
        logger.debug('[PDFEditor] Scroll ended');
        updateCurrentPageFromScroll();
      }, 150);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollEndTimeout.current) clearTimeout(scrollEndTimeout.current);
      if (scrollRafId.current) cancelAnimationFrame(scrollRafId.current);
    };
  }, [containerRef, setIsScrolling, updateCurrentPageFromScroll]);

  useEffect(() => {
    if (!numPages) return;

    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isViewportChanging) {
          logger.debug(
            '[PDFEditor] Skipping visibility update during viewport change',
          );
          return;
        }

        if (debounceTimeout) clearTimeout(debounceTimeout);

        debounceTimeout = setTimeout(() => {
          setVisiblePages((prev) => {
            const newSet = new Set(prev);
            let updated = false;

            entries.forEach((entry) => {
              const pageNum = parseInt(
                entry.target.getAttribute('data-page-number') || '0',
              );
              if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
                if (!newSet.has(pageNum)) {
                  logger.debug(`[PDFEditor] Page ${pageNum} became visible`);
                  newSet.add(pageNum);
                  updated = true;
                }
              } else if (newSet.has(pageNum)) {
                logger.debug(`[PDFEditor] Page ${pageNum} became invisible`);
                newSet.delete(pageNum);
                updated = true;
              }
            });

            if (updated && newSet.size === 0) {
              logger.debug(
                '[PDFEditor] Prevented empty visible pages set; preserving previous visible pages',
              );
              return prev.size > 0 ? prev : new Set([1]);
            }
            if (updated && logger.isDebugEnabled()) {
              logger.debug(
                '[PDFEditor] Visible pages updated:',
                Array.from(newSet),
              );
            }
            return updated ? newSet : prev;
          });
        }, 200);
      },
      {
        root: containerRef.current,
        rootMargin: '100px 0px',
        threshold: [0, 0.1, 0.5, 1.0],
      },
    );

    const timeoutId = setTimeout(() => {
      const pageElements =
        containerRef.current?.querySelectorAll('[data-page-number]');
      pageElements?.forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (debounceTimeout) clearTimeout(debounceTimeout);
      observer.disconnect();
    };
  }, [containerRef, isViewportChanging, numPages, setVisiblePages]);
};
